
    import { useState, useCallback } from 'react';
    import { getSupabase } from '@/lib/supabaseClient.js';
    import { useToast } from "@/components/ui/use-toast";

    export const useAvatarUpload = (userId) => {
      const supabase = getSupabase();
      const [isUploading, setIsUploading] = useState(false);
      const { toast } = useToast();

      const uploadAvatar = useCallback(async (file) => {
        if (!userId) {
          toast({ title: "Error", description: "User not logged in.", variant: "destructive" });
          return null;
        }
        if (!file) {
          toast({ title: "Error", description: "No file selected.", variant: "destructive" });
          return null;
        }
        if (!supabase) {
          toast({ title: "Error", description: "Supabase client not available.", variant: "destructive" });
          console.warn("[useAvatarUpload] Supabase client not available.");
          return null;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          toast({ 
            title: "Invalid File Type", 
            description: "Please select a JPEG or PNG image. WebP and other formats are not supported.", 
            variant: "destructive" 
          });
          return null;
        }

        setIsUploading(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}-${Date.now()}.${fileExt}`;
        let filePath = `avatars/${fileName}`;

        // Check if storage bucket exists
        try {
          const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
          if (bucketsError) {
            console.error('[useAvatarUpload] Error listing buckets:', bucketsError);
          } else {
            console.log('[useAvatarUpload] Available buckets:', buckets);
            const avatarsBucket = buckets?.find(bucket => bucket.name === 'avatars');
            if (!avatarsBucket) {
              console.warn('[useAvatarUpload] Avatars bucket not found, available buckets:', buckets);
            }
          }
        } catch (error) {
          console.warn('[useAvatarUpload] Could not check buckets:', error);
        }

        try {
          console.log('[useAvatarUpload] Starting upload for file:', file.name, 'to path:', filePath);
          
          // Try to upload with explicit content type
          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, file, {
              contentType: file.type,
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('[useAvatarUpload] Upload error:', uploadError);
            
            // If it's a RLS error, try a different approach
            if (uploadError.message?.includes('row-level security policy')) {
              console.log('[useAvatarUpload] RLS error detected, trying alternative approach...');
              
              // Try uploading to a different path or with different options
              const alternativePath = `public-avatars/${fileName}`;
              const { error: altUploadError } = await supabase.storage
                .from('avatars')
                .upload(alternativePath, file, {
                  contentType: file.type,
                  cacheControl: '3600',
                  upsert: false
                });
              
              if (altUploadError) {
                throw altUploadError;
              }
              
              // Update the filePath for URL generation
              filePath = alternativePath;
            } else {
              throw uploadError;
            }
          }

          console.log('[useAvatarUpload] Upload successful');

          // Try to get public URL first
          const { data: urlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);

          let finalUrl = urlData?.publicUrl;
          console.log('[useAvatarUpload] Public URL data:', urlData);

          // If no public URL, try signed URL
          if (!finalUrl) {
            console.log('[useAvatarUpload] No public URL, trying signed URL...');
            const { data: signedData, error: signedError } = await supabase.storage
              .from('avatars')
              .createSignedUrl(filePath, 3600);
            
            console.log('[useAvatarUpload] Signed URL data:', signedData, 'error:', signedError);
            
            if (signedError || !signedData?.signedUrl) {
              throw signedError || new Error('Could not generate signed URL for uploaded avatar.');
            }
            finalUrl = signedData.signedUrl;
          }

          if (!finalUrl) {
            throw new Error('Could not obtain URL for uploaded avatar.');
          }

          console.log('[useAvatarUpload] Final URL:', finalUrl);

          return finalUrl;

        } catch (error) {
          console.error('Error uploading avatar:', error);
          toast({ title: "Upload Failed", description: error.message || "Could not upload avatar.", variant: "destructive" });
          return null; 
        } finally {
          setIsUploading(false);
        }
      }, [userId, toast, supabase]);

      return { uploadAvatar, isUploading };
    };
  